import React, { ReactElement } from "react";
import { Grid } from "@mui/material";
import {
  AccountBalanceOutlined,
  AssignmentTurnedInOutlined,
  ChangeCircleOutlined,
  Close,
  Create,
  EmailOutlined,
  HourglassEmptyOutlined,
  Launch,
  OpenInBrowser,
  PersonAdd,
  RotateLeft,
  SendOutlined,
} from "@mui/icons-material";
import EmojiEvents from "../../../assets/icon/emoji_events-24px.svg";
import { translate } from "../../../services/Translations/translatorService";
import { CurrentAndPreviousState, HistoryLog } from "../../../types/HistoryLog";
import GenericLogEntry from "./GenericLogEntry";
import CaseHistoryInvoiceTypeIdentifierChange from "./CustomCaseHistoryItem/CaseHistoryInvoiceTypeIdentifierChange";
import CaseHistoryInvoiceDelete from "./CustomCaseHistoryItem/CaseHistoryInvoiceDelete";
import CaseHistoryInvoiceReassign from "./CustomCaseHistoryItem/CaseHistoryInvoiceReassign";
import CaseHistoryLeadStatusChange from "./CustomCaseHistoryItem/CaseHistoryLeadStatusChange";
import CaseHistoryLeadStatusChangeByCustomerPrevented from "./CustomCaseHistoryItem/CaseHistoryLeadStatusChangeByCustomerPrevented";
import { stripHtml } from "string-strip-html";
import { ProductUnion } from "../../../types/ProductUnion";
import EntityHistoryLogEntry from "./EntityHistoryLogEntry";

interface CaseHistoryItemProps {
  historyItem: HistoryLog;
  product: ProductUnion;
}

const getChangesPreview = (changes: string[]): string => changes.join(", ").slice(0, 100);

const CaseHistoryItem = ({ historyItem, product }: CaseHistoryItemProps): null | ReactElement => {
  const { productClassName } = product;

  switch (historyItem.type) {
    // GENERIC LOG ENTRY DISPLAY COMPONENTS
    case "customerRegistration":
      return (
        <GenericLogEntry
          subject={translate("historyItem.customerRegistration.label")}
          Icon={PersonAdd}
          historyItem={historyItem}
        />
      );
    case "caseCreated":
      return (
        <GenericLogEntry subject={translate("historyItem.caseCreated.label")} Icon={Launch} historyItem={historyItem} />
      );
    case "entityStatusChange":
      return <CaseHistoryLeadStatusChange historyItem={historyItem} productClassName={productClassName} />;
    case "entityStatusChangeByCustomerPrevented":
      return (
        <CaseHistoryLeadStatusChangeByCustomerPrevented historyItem={historyItem} productClassName={productClassName} />
      );
    case "assignedUserChange": {
      const [role, assignment] = Object.entries(historyItem.logData ?? {})[0] ?? [];

      return (
        <GenericLogEntry
          subject={`${translate("historyItem.assignedUserChange.label")}: ${role ? `${translate(`historyItem.${role}.label`)} ` : ""}${assignment?.previous ?? "Leer"} → ${assignment?.current ?? "Leer"}`}
          Icon={Create}
          historyItem={historyItem}
        />
      );
    }
    case "customerDataChange": {
      const changes = Object.entries(historyItem.logData).map(
        ([key, logItem]: [key: string, logItem: CurrentAndPreviousState]) => {
          const emptyValue = key === "gender" || key.endsWith(".gender") ? "Keine Anrede" : "Leer";
          const previous = logItem.previous
            ? translate(`customer.${key}.values.${logItem.previous}`, String(logItem.previous))
            : emptyValue;
          const current = logItem.current
            ? translate(`customer.${key}.values.${logItem.current}`, String(logItem.current))
            : emptyValue;

          return { key, label: translate(`customer.${key}.label`), previous, current };
        },
      );

      return (
        <GenericLogEntry
          subject={`${translate("historyItem.customerDataChange.label")}: ${getChangesPreview(
            changes.map((change) => `${change.label}: ${change.previous} → ${change.current}`),
          )}`}
          Icon={Create}
          info={changes.map((change) => (
            <div key={change.key}>
              {change.label}: {change.previous} → {change.current}
            </div>
          ))}
          historyItem={historyItem}
        />
      );
    }
    case "entityDataChange": {
      const changes = Object.entries(historyItem.logData).map(([key, value]: [key: string, value: any]) => {
        const emptyValue = key === "gender" || key.endsWith(".gender") ? "Keine Anrede" : "Leer";
        const previous = stripHtml(
          !!value.previous || value.previous === false
            ? translate(`${productClassName}.${key}.values.${value.previous}`, String(value.previous))
            : emptyValue,
        ).result;
        const current = stripHtml(
          !!value.current || value.current === false
            ? translate(`${productClassName}.${key}.values.${value.current}`, String(value.current))
            : emptyValue,
        ).result;

        return { key, label: translate(`${productClassName}.${key}.label`), previous, current };
      });

      return (
        <GenericLogEntry
          subject={`${translate("historyItem.entityDataChange.label")}: ${getChangesPreview(
            changes.map((change) => `${change.label}: ${change.previous} → ${change.current}`),
          )}`}
          Icon={Create}
          info={changes.map((change) => (
            <div key={change.key}>
              <Grid container>
                <Grid>{change.label}:&nbsp;</Grid>
                <Grid>{change.previous}</Grid>
                <Grid>&nbsp;{"→"}&nbsp;</Grid>
                <Grid>{change.current}</Grid>
              </Grid>
            </div>
          ))}
          historyItem={historyItem}
        />
      );
    }
    case "activity":
    case "beaMessage":
    case "emailMessage":
    case "fax":
    case "inquiryToCustomer":
    case "mediaObject":
    case "mediaObjectDelete":
    case "mediaObjectIdentifierChange":
    case "message":
    case "note":
    case "paidTask":
      return <EntityHistoryLogEntry historyItem={historyItem} product={product} />;
    case "beaOrFaxPause":
      return (
        <GenericLogEntry
          subject={`Automatische beA/Faxe ${
            historyItem.logData.preventAllSystemBeaOrFax ? "pausiert" : "wieder angeschaltet"
          }`}
          Icon={SendOutlined}
          historyItem={historyItem}
        />
      );
    case "passwordReset":
      return (
        <GenericLogEntry subject={"Passwort-Setzen-E-Mail versendet"} Icon={EmailOutlined} historyItem={historyItem} />
      );
    //this is here historically, functionality was removed
    case "activitiesPause":
      return (
        <GenericLogEntry
          subject={`Setzen von Aktivitäten ${
            historyItem.logData.preventAllSystemActivities ? "pausiert" : "wieder angeschaltet"
          }`}
          Icon={AssignmentTurnedInOutlined}
          historyItem={historyItem}
        />
      );
    case "caseActivityCheck":
      return (
        <GenericLogEntry
          subject={`Automatischer Statuscheck ${
            historyItem.logData.shouldCheckCaseActivity
              ? `aktiviert - Häufigkeit: ${historyItem.logData.checkCaseActivityIntervalInMonths?.current} Monat${
                  historyItem.logData.checkCaseActivityIntervalInMonths?.current === 1 ? "" : "e"
                }`
              : "deaktiviert"
          }`}
          Icon={RotateLeft}
          historyItem={historyItem}
        />
      );
    case "caseStatusChange":
      switch (historyItem.logData.caseStatus?.current) {
        case "inactive":
          return (
            <GenericLogEntry
              subject={translate("historyItem.caseStatusChange.inactive.label")}
              Icon={HourglassEmptyOutlined}
              historyItem={historyItem}
            />
          );
        case "lost":
          return (
            <GenericLogEntry
              subject={translate("historyItem.caseStatusChange.lost.label")}
              Icon={Close}
              info={
                historyItem.logData.caseStatus?.current === "lost" && (
                  <>
                    {historyItem.logData?.lostCategory?.current && (
                      <div>
                        Verlustkategorie:{" "}
                        {translate(
                          "historyItem.caseStatusChange.lost.lostCategory." + historyItem.logData.lostCategory.current,
                        )}
                      </div>
                    )}
                    {historyItem.logData?.lostReason?.current && (
                      <div>Verlustgrund: {historyItem.logData.lostReason.current}</div>
                    )}
                  </>
                )
              }
              historyItem={historyItem}
            />
          );
        case "won":
          return (
            <GenericLogEntry
              subject={translate("historyItem.caseStatusChange.won.label")}
              Icon={EmojiEvents}
              iconType={"iconFile"}
              historyItem={historyItem}
            />
          );
        case "open":
          return (
            <GenericLogEntry
              subject={translate("historyItem.caseStatusChange.open.label")}
              Icon={OpenInBrowser}
              historyItem={historyItem}
            />
          );
        default:
          return null;
      }
    // HISTORY LOGS EXTRACTED FROM 'entityDataChange' ONES (SPOOFED)
    case "accountingPause":
      return (
        <GenericLogEntry
          subject={`Automatische Konto/Rechnungen ${
            historyItem.logData.preventAccounting.current ? "pausiert" : "wieder angeschaltet"
          }`}
          Icon={AccountBalanceOutlined}
          historyItem={historyItem}
        />
      );

    case "mailPause":
      return (
        <GenericLogEntry
          subject={`Automatische Emails und Nachrichten an die Mandantschaft ${
            historyItem.logData.preventAllSystemEmails!.current ? "pausiert" : "wieder angeschaltet"
          }`}
          Icon={EmailOutlined}
          historyItem={historyItem}
        />
      );
    case "invoiceTypeIdentifierChange":
      return <CaseHistoryInvoiceTypeIdentifierChange productClassName={productClassName} historyItem={historyItem} />;
    case "invoiceDelete":
      return <CaseHistoryInvoiceDelete historyItem={historyItem} />;
    case "invoiceReassign":
      return <CaseHistoryInvoiceReassign historyItem={historyItem} />;
    case "productChange":
      return (
        <GenericLogEntry
          subject={`Produkt gewechselt: ${translate(`${historyItem.logData.oldProductType}.label`)} → ${translate(
            `${historyItem.logData.newProductType}.label`,
          )}`}
          Icon={ChangeCircleOutlined}
          historyItem={historyItem}
          userNameFallback={translate("historyItem.client")}
        />
      );
    default:
      return null;
  }
};

export default CaseHistoryItem;
